import React, { useState } from "react";
import {
  Input,
  Button,
  Modal,
  Typography,
  Checkbox,
  Tooltip,
  message,
} from "antd";
import { PlusOutlined, InfoCircleOutlined } from "@ant-design/icons";
import "./style.scss";

const { TextArea } = Input;
const { Text } = Typography;

const PincodeInput = ({
  pincodes,
  onChange,
  isBookingOnlyVisible = true,
  isBookingOnly,
  setIsBookingOnly,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [bulkInput, setBulkInput] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    if (!inputValue.trim()) {
      message.error("Please enter a pincode");
      return;
    }

    if (!/^\d{6}$/.test(inputValue)) {
      message.error("Pincode must be a 6-digit number");
      return;
    }

    const numericPincode = parseInt(inputValue, 10);

    if (pincodes.find((e) => e.pincode === numericPincode)) {
      message.warning("This pincode has already been added");
      setInputValue("");
      return;
    }

    const newPincodes = [numericPincode];
    onChange(newPincodes, isBookingOnly);
    setInputValue("");
  };

  const showModal = () => {
    setIsModalVisible(true);
    setBulkInput("");
    setErrorMessage("");
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleBulkInputChange = (e) => {
    setBulkInput(e.target.value);
    setErrorMessage("");
  };

  const processBulkPincodes = () => {
    if (!bulkInput.trim()) {
      setErrorMessage("Please enter at least one pincode");
      return;
    }

    // Split by commas, line breaks, or spaces and filter out empty strings
    const rawPincodes = bulkInput
      .split(/[\s,\n]+/)
      .map((code) => code.trim())
      .filter((code) => code.length > 0);

    // Validate each pincode
    const validPincodes = [];
    const invalidPincodes = [];
    const duplicatePincodes = [];

    rawPincodes.forEach((pincode) => {
      if (/^\d{6}$/.test(pincode)) {
        const numericPincode = parseInt(pincode, 10);

        if (pincodes.find((e) => e.pincode === numericPincode)) {
          duplicatePincodes.push(pincode);
        } else if (validPincodes.includes(numericPincode)) {
          duplicatePincodes.push(pincode);
        } else {
          validPincodes.push(numericPincode);
        }
      } else if (pincode.length > 0) {
        invalidPincodes.push(pincode);
      }
    });

    // Build error messages
    let errorMsgs = [];

    if (invalidPincodes.length > 0) {
      errorMsgs.push(
        `Invalid pincodes (must be 6 digits): ${invalidPincodes.join(", ")}`
      );
    }

    if (duplicatePincodes.length > 0) {
      errorMsgs.push(`Duplicate pincodes: ${duplicatePincodes.join(", ")}`);
    }

    if (validPincodes.length === 0) {
      errorMsgs.push("No valid pincodes found to add");
    }

    if (errorMsgs.length > 0) {
      setErrorMessage(errorMsgs.join("\n"));
      return;
    }

    const newPincodes = [...validPincodes];
    onChange(newPincodes, isBookingOnly);
    setIsModalVisible(false);
  };

  return (
    <div className="pincode-input-container">
      <div className="input-row">
        <Input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onPressEnter={handleInputConfirm}
          placeholder="Enter 6-digit pincode"
          maxLength={6}
          className="pincode-input"
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleInputConfirm}
          className="bulk-add-button"
        >
          Add
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={showModal}
          className="bulk-add-button"
        >
          Bulk Add
        </Button>

        {isBookingOnlyVisible && (
          <span className="checkbox-with-info">
            <Checkbox
              checked={isBookingOnly}
              onChange={(e) => setIsBookingOnly(e.target.checked)}
              className="checkbox-item"
            >
              Mark for Booking Services Only
            </Checkbox>
            <Tooltip
              title="When enabled, all pincodes will be marked for booking services only."
              placement="top"
            >
              <InfoCircleOutlined className="info-icon" />
            </Tooltip>
          </span>
        )}
      </div>

      <Modal
        title="Add Bulk Pincodes"
        open={isModalVisible}
        onOk={processBulkPincodes}
        onCancel={handleCancel}
        okText="Add Pincodes"
        width={500}
      >
        <div className="bulk-input-container">
          <Text>
            Enter multiple 6-digit pincodes (separated by commas, spaces, or new
            lines):
          </Text>
          <TextArea
            value={bulkInput}
            onChange={handleBulkInputChange}
            placeholder="e.g. 301001, 301002, 301705&#10;302001 302002 302003&#10;302004"
            rows={8}
            className="bulk-input"
          />
          {errorMessage && (
            <div className="error-message">
              {errorMessage.split("\n").map((error, index) => (
                <Text key={index} type="danger" style={{ display: "block" }}>
                  {error}
                </Text>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PincodeInput;
